import { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { productsAPI, recipesAPI, portionTransfersAPI } from '../lib/api';
import { formatCurrency, formatNumber, getCategoryLabel } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, PencilSimple, Trash, CookingPot, X, MagnifyingGlass, 
  Factory, ArrowRight, ClockCounterClockwise, Warning, CheckCircle,
  ArrowCounterClockwise, FolderPlus, Gear
} from '@phosphor-icons/react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

// Eski hardcoded kategoriler - geriye uyumluluk için
const legacyCategories = [
  { value: 'ana_yemek', label: 'Ana Yemek' },
  { value: 'baslangic', label: 'Başlangıç' },
  { value: 'tatli', label: 'Tatlı' },
  { value: 'icecekler', label: 'İçecekler' },
];

const COLORS = ['#D15E36', '#6E8A72', '#E6BE78', '#C94A4A', '#78746D'];

export default function Recipes() {
  const [recipes, setRecipes] = useState([]);
  const [products, setProducts] = useState([]);
  const [portionTransfers, setPortionTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productionDialogOpen, setProductionDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [rollbackDialogOpen, setRollbackDialogOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [deletingRecipe, setDeletingRecipe] = useState(null);
  const [producingRecipe, setProducingRecipe] = useState(null);
  const [rollingBackTransfer, setRollingBackTransfer] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [productionQuantity, setProductionQuantity] = useState('');
  const [productionNotes, setProductionNotes] = useState('');
  const [rollbackReason, setRollbackReason] = useState('');
  const [productionLoading, setProductionLoading] = useState(false);
  
  // Dinamik kategori yönetimi (Faz 2B)
  const [recipeCategories, setRecipeCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]); // Yönetim dialogu için tüm kategoriler
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryManageDialogOpen, setCategoryManageDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    selling_price: '',
    portions_per_recipe: '1',
    ingredients: [],
    recipe_type: 'cost_only',
    output_product_id: '',
  });

  const [ingredientForm, setIngredientForm] = useState({
    product_id: '',
    quantity: '',
    ingredient_type: 'product',  // 'product' | 'recipe'
    recipe_id: '',
  });

  // Ingredient arama için state'ler
  const [ingredientSearchOpen, setIngredientSearchOpen] = useState(false);
  const [ingredientSearchQuery, setIngredientSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      // include_inactive=true ile tüm kategorileri al (yönetim dialogu için pasifler de dahil)
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recipe-categories?include_inactive=true`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        // Tüm kategorileri sakla (yönetim dialogu için)
        const all = data.map(c => ({
          value: c.name,
          label: c.name,
          id: c.id,
          description: c.description,
          recipe_count: c.recipe_count,
          is_active: c.is_active !== false,
        }));
        setAllCategories(all);
        
        // Sadece aktif kategorileri dropdown'larda göster
        const activeCategories = all.filter(c => c.is_active);
        setRecipeCategories(activeCategories);
      } else {
        // API hatası durumunda legacy kategorileri kullan
        setRecipeCategories(legacyCategories);
        setAllCategories(legacyCategories.map(c => ({ ...c, is_active: true })));
      }
    } catch (error) {
      console.error('Kategori yükleme hatası:', error);
      setRecipeCategories(legacyCategories);
      setAllCategories(legacyCategories.map(c => ({ ...c, is_active: true })));
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Kategori adı gerekli');
      return;
    }
    
    setCategoryLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recipe-categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          name: newCategoryName.trim(),
          description: newCategoryDescription.trim() || null,
        }),
      });
      
      if (response.ok) {
        const newCat = await response.json();
        const newCategoryValue = newCat.name;
        toast.success(`'${newCategoryValue}' kategorisi oluşturuldu`);
        
        // Dialog'u önce kapat ve inputları temizle
        setCategoryDialogOpen(false);
        setNewCategoryName('');
        setNewCategoryDescription('');
        
        // Kategori listesini güncelle
        await fetchCategories();
        
        // Yeni kategoriyi seç (microtask ile Radix Select'in re-render'ını bekle)
        setTimeout(() => {
          setFormData(prev => ({ ...prev, category: newCategoryValue }));
        }, 0);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Kategori oluşturulamadı');
      }
    } catch (error) {
      toast.error('Kategori oluşturma hatası');
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory || !newCategoryName.trim()) return;
    
    setCategoryLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recipe-categories/${editingCategory.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          name: newCategoryName.trim(),
          description: newCategoryDescription.trim() || null,
          is_active: editingCategory.is_active,
        }),
      });
      
      if (response.ok) {
        toast.success('Kategori güncellendi');
        await fetchCategories();
        setEditingCategory(null);
        setNewCategoryName('');
        setNewCategoryDescription('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Kategori güncellenemedi');
      }
    } catch (error) {
      toast.error('Güncelleme hatası');
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleToggleCategoryActive = async (category) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recipe-categories/${category.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          is_active: !category.is_active,
        }),
      });
      
      if (response.ok) {
        toast.success(category.is_active ? 'Kategori pasife alındı' : 'Kategori aktifleştirildi');
        await fetchCategories();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'İşlem başarısız');
      }
    } catch (error) {
      toast.error('İşlem hatası');
    }
  };

  const handleDeleteCategory = async (category) => {
    if (category.recipe_count > 0) {
      toast.error(`Bu kategori ${category.recipe_count} reçetede kullanılıyor. Önce pasife alın.`);
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recipe-categories/${category.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        toast.success('Kategori silindi');
        await fetchCategories();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Kategori silinemedi');
      }
    } catch (error) {
      toast.error('Silme hatası');
    }
  };

  const fetchData = async () => {
    try {
      const [recipesRes, productsRes, transfersRes] = await Promise.all([
        recipesAPI.getAll(),
        productsAPI.getAll(),
        portionTransfersAPI.getAll({ limit: 50 }),
      ]);
      setRecipes(recipesRes.data);
      setProducts(productsRes.data);
      setPortionTransfers(transfersRes.data);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (recipe = null) => {
    if (recipe) {
      setEditingRecipe(recipe);
      setFormData({
        name: recipe.name,
        category: recipe.category,
        description: recipe.description || '',
        selling_price: recipe.selling_price.toString(),
        portions_per_recipe: (recipe.portions_per_recipe || 1).toString(),
        ingredients: recipe.ingredients.map(ing => ({
          ingredient_type: ing.ingredient_type || 'product',
          product_id: ing.product_id || '',
          recipe_id: ing.recipe_id || '',
          quantity: ing.quantity,
        })),
        recipe_type: recipe.recipe_type || 'cost_only',
        output_product_id: recipe.output_product_id || '',
      });
    } else {
      setEditingRecipe(null);
      // İlk aktif kategoriyi varsayılan olarak seç
      const defaultCategory = recipeCategories.length > 0 ? recipeCategories[0].value : 'Ana Yemek';
      setFormData({
        name: '',
        category: defaultCategory,
        description: '',
        selling_price: '',
        portions_per_recipe: '1',
        ingredients: [],
        recipe_type: 'cost_only',
        output_product_id: '',
      });
    }
    setIngredientForm({ product_id: '', quantity: '', ingredient_type: 'product', recipe_id: '' });
    setDialogOpen(true);
  };

  const handleOpenProductionDialog = (recipe) => {
    setProducingRecipe(recipe);
    setProductionQuantity('');
    setProductionNotes('');
    setProductionDialogOpen(true);
  };

  const handleAddIngredient = () => {
    const isRecipe = ingredientForm.ingredient_type === 'recipe';
    const selectedId = isRecipe ? ingredientForm.recipe_id : ingredientForm.product_id;
    
    if (!selectedId || !ingredientForm.quantity) {
      toast.error('Lütfen malzeme/reçete ve miktar seçin');
      return;
    }

    // Duplicate kontrolü
    const existing = formData.ingredients.find(i => {
      if (isRecipe) {
        return i.ingredient_type === 'recipe' && i.recipe_id === selectedId;
      } else {
        return (!i.ingredient_type || i.ingredient_type === 'product') && i.product_id === selectedId;
      }
    });
    
    if (existing) {
      toast.error('Bu malzeme/reçete zaten eklenmiş');
      return;
    }

    // Kendini ekleme kontrolü (düzenleme modunda)
    if (isRecipe && editingRecipe && selectedId === editingRecipe.id) {
      toast.error('Reçete kendisini alt reçete olarak içeremez');
      return;
    }

    // Çıktı ürün kontrolü (sadece ürün türü için)
    if (!isRecipe && formData.recipe_type === 'portion_transfer' && selectedId === formData.output_product_id) {
      toast.error('Çıktı ürün, hammadde olarak eklenemez');
      return;
    }

    const newIngredient = isRecipe
      ? {
          ingredient_type: 'recipe',
          recipe_id: selectedId,
          quantity: parseFloat(ingredientForm.quantity),
        }
      : {
          ingredient_type: 'product',
          product_id: selectedId,
          quantity: parseFloat(ingredientForm.quantity),
        };

    setFormData({
      ...formData,
      ingredients: [...formData.ingredients, newIngredient],
    });
    setIngredientForm({ product_id: '', quantity: '', ingredient_type: 'product', recipe_id: '' });
  };

  const handleRemoveIngredient = (ing) => {
    setFormData({
      ...formData,
      ingredients: formData.ingredients.filter(i => {
        if (ing.ingredient_type === 'recipe') {
          return !(i.ingredient_type === 'recipe' && i.recipe_id === ing.recipe_id);
        } else {
          return !((!i.ingredient_type || i.ingredient_type === 'product') && i.product_id === ing.product_id);
        }
      }),
    });
  };

  const calculateTotalCost = () => {
    return formData.ingredients.reduce((total, ing) => {
      const ingredientType = ing.ingredient_type || 'product';
      
      if (ingredientType === 'recipe') {
        // Alt reçete maliyeti: cost_per_portion * quantity
        const subRecipe = recipes.find(r => r.id === ing.recipe_id);
        if (subRecipe) {
          return total + ((subRecipe.cost_per_portion || 0) * ing.quantity);
        }
      } else {
        // Ürün maliyeti
        const product = products.find(p => p.id === ing.product_id);
        if (product) {
          return total + (product.unit_price * ing.quantity);
        }
      }
      return total;
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.selling_price || formData.ingredients.length === 0) {
      toast.error('Lütfen gerekli alanları doldurun ve en az bir malzeme ekleyin');
      return;
    }

    // portion_transfer için çıktı ürün zorunlu
    if (formData.recipe_type === 'portion_transfer' && !formData.output_product_id) {
      toast.error('Porsiyon transfer reçeteleri için çıktı ürün seçmeniz zorunludur');
      return;
    }

    const data = {
      name: formData.name,
      category: formData.category,
      description: formData.description || undefined,
      selling_price: parseFloat(formData.selling_price),
      portions_per_recipe: parseInt(formData.portions_per_recipe) || 1,
      ingredients: formData.ingredients,
      recipe_type: formData.recipe_type,
      output_product_id: formData.recipe_type === 'portion_transfer' ? formData.output_product_id : null,
    };

    try {
      if (editingRecipe) {
        await recipesAPI.update(editingRecipe.id, data);
        toast.success('Reçete güncellendi');
      } else {
        await recipesAPI.create(data);
        toast.success('Reçete oluşturuldu');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleDelete = async () => {
    if (!deletingRecipe) return;
    
    try {
      await recipesAPI.delete(deletingRecipe.id);
      toast.success('Reçete silindi');
      setDeleteDialogOpen(false);
      setDeletingRecipe(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme işlemi başarısız');
    }
  };

  // Porsiyon üretimi
  const handleProduction = async () => {
    if (!producingRecipe || !productionQuantity) return;
    
    const qty = parseInt(productionQuantity);
    if (qty <= 0) {
      toast.error('Porsiyon sayısı pozitif olmalı');
      return;
    }

    setProductionLoading(true);
    try {
      await recipesAPI.transferPortions(producingRecipe.id, {
        quantity: qty,
        notes: productionNotes || undefined,
      });
      toast.success(`${qty} porsiyon üretildi!`);
      setProductionDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Üretim başarısız');
    } finally {
      setProductionLoading(false);
    }
  };

  // Rollback işlemi
  const handleRollback = async () => {
    if (!rollingBackTransfer || !rollbackReason.trim()) {
      toast.error('Geri alma nedeni zorunludur');
      return;
    }

    try {
      await portionTransfersAPI.rollback(rollingBackTransfer.production_id, {
        reason: rollbackReason,
      });
      toast.success('Üretim geri alındı');
      setRollbackDialogOpen(false);
      setRollingBackTransfer(null);
      setRollbackReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Geri alma başarısız');
    }
  };

  // Üretim için gerekli stok hesaplama
  const getProductionRequirements = () => {
    if (!producingRecipe || !productionQuantity) return [];
    
    const qty = parseInt(productionQuantity) || 0;
    return producingRecipe.ingredients.map(ing => {
      const product = products.find(p => p.id === ing.product_id);
      const required = ing.quantity * qty;
      const available = product?.prep_stock || 0;
      return {
        product_id: ing.product_id,
        product_name: product?.name || 'Bilinmiyor',
        unit: product?.unit || '',
        required,
        available,
        sufficient: available >= required,
      };
    });
  };

  const productionRequirements = getProductionRequirements();
  const canProduce = productionRequirements.length > 0 && productionRequirements.every(r => r.sufficient);

  const filteredRecipes = recipes.filter((r) => {
    const matchesSearch = r.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || r.category === categoryFilter;
    const matchesType = typeFilter === 'all' || r.recipe_type === typeFilter;
    return matchesSearch && matchesCategory && matchesType;
  });

  const totalCost = calculateTotalCost();
  const portions = parseInt(formData.portions_per_recipe) || 1;
  const costPerPortion = totalCost / portions;
  const profit = formData.selling_price ? parseFloat(formData.selling_price) - costPerPortion : 0;
  const profitMargin = formData.selling_price && parseFloat(formData.selling_price) > 0 
    ? (profit / parseFloat(formData.selling_price) * 100) 
    : 0;

  // Çıktı ürün seçenekleri (hammaddeler hariç)
  const outputProductOptions = products.filter(p => 
    !formData.ingredients.some(ing => ing.product_id === p.id)
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">
              Reçeteler
            </h1>
            <p className="mt-2 text-muted-foreground">
              Menü ürünlerinizin reçetelerini, maliyetlerini ve porsiyon üretimini yönetin
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setHistoryDialogOpen(true)}
              data-testid="production-history-btn"
            >
              <ClockCounterClockwise size={20} weight="bold" className="mr-2" />
              Üretim Geçmişi
            </Button>
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-primary hover:bg-primary/90"
              data-testid="add-recipe-button"
            >
              <Plus size={20} weight="bold" className="mr-2" />
              Yeni Reçete
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Reçete ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-11"
              data-testid="search-recipes"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full sm:w-48 h-11" data-testid="recipe-category-filter">
              <SelectValue placeholder="Kategori" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Kategoriler</SelectItem>
              {recipeCategories.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-48 h-11" data-testid="recipe-type-filter">
              <SelectValue placeholder="Tip" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Tipler</SelectItem>
              <SelectItem value="cost_only">Sadece Maliyet</SelectItem>
              <SelectItem value="portion_transfer">Porsiyon Transfer</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Recipes Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRecipes.length === 0 ? (
            <div className="col-span-full text-center py-12 border border-border bg-card rounded-xl">
              <CookingPot size={48} weight="duotone" className="mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">Henüz reçete bulunmuyor</p>
            </div>
          ) : (
            filteredRecipes.map((recipe) => (
              <div
                key={recipe.id}
                className="border border-border bg-card rounded-xl overflow-hidden card-hover"
                data-testid={`recipe-card-${recipe.id}`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex gap-2 mb-2">
                        <span className="badge-pill bg-muted text-muted-foreground text-xs">
                          {getCategoryLabel(recipe.category)}
                        </span>
                        {recipe.recipe_type === 'portion_transfer' && (
                          <span className="badge-pill bg-amber-100 text-amber-700 text-xs">
                            <Factory size={12} className="inline mr-1" />
                            Üretim
                          </span>
                        )}
                      </div>
                      <h3 className="font-heading text-xl font-semibold">{recipe.name}</h3>
                      {recipe.output_product_name && (
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <ArrowRight size={12} />
                          Çıktı: {recipe.output_product_name}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {recipe.recipe_type === 'portion_transfer' && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleOpenProductionDialog(recipe)}
                          className="bg-amber-600 hover:bg-amber-700"
                          data-testid={`produce-recipe-${recipe.id}`}
                        >
                          <Factory size={16} className="mr-1" />
                          Üret
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenDialog(recipe)}
                        data-testid={`edit-recipe-${recipe.id}`}
                      >
                        <PencilSimple size={18} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setDeletingRecipe(recipe);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-recipe-${recipe.id}`}
                      >
                        <Trash size={18} />
                      </Button>
                    </div>
                  </div>

                  {recipe.description && (
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {recipe.description}
                    </p>
                  )}

                  <div className="space-y-2 mb-4">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">
                      Malzemeler ({recipe.ingredients.length})
                    </p>
                    {/* Veri uyarısı kontrolü */}
                    {recipe.ingredients.some(ing => ing.data_warning) && (
                      <div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded flex items-center gap-1">
                        <span>⚠️</span>
                        <span>Bazı malzemelerde veri tutarsızlığı var</span>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-1">
                      {recipe.ingredients.slice(0, 4).map((ing) => {
                        const isRecipe = ing.ingredient_type === 'recipe';
                        const name = isRecipe ? ing.recipe_name : ing.product_name;
                        const hasWarning = ing.data_warning;
                        return (
                          <span 
                            key={isRecipe ? `r-${ing.recipe_id}` : `p-${ing.product_id}`} 
                            className={`text-xs px-2 py-1 rounded ${hasWarning ? 'bg-amber-100 text-amber-800' : 'bg-muted'}`}
                            title={hasWarning ? ing.data_warning : undefined}
                          >
                            {isRecipe ? '📋' : ''}{name || 'Bilinmeyen'} ({formatNumber(ing.quantity)} {ing.unit})
                          </span>
                        );
                      })}
                      {recipe.ingredients.length > 4 && (
                        <span className="text-xs bg-muted px-2 py-1 rounded">
                          +{recipe.ingredients.length - 4}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-border">
                    {recipe.portions_per_recipe > 1 && (
                      <p className="text-xs text-center text-muted-foreground mb-2">
                        Bu reçeteden <strong>{recipe.portions_per_recipe}</strong> porsiyon çıkar
                      </p>
                    )}
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <div>
                        <p className="text-xs text-muted-foreground">Top. Maliyet</p>
                        <p className="font-data font-semibold text-sm">
                          {formatCurrency(recipe.total_cost)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Pors. Maliyet</p>
                        <p className="font-data font-semibold text-sm text-primary">
                          {formatCurrency(recipe.cost_per_portion || recipe.total_cost)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Satış</p>
                        <p className="font-data font-semibold text-sm">
                          {formatCurrency(recipe.selling_price)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Kar</p>
                        <p className={`font-data font-semibold text-sm ${recipe.profit >= 0 ? 'text-secondary' : 'text-destructive'}`}>
                          %{formatNumber(recipe.profit_margin, 1)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingRecipe ? 'Reçete Düzenle' : 'Yeni Reçete Oluştur'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column - Recipe Info */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                      Reçete Adı
                    </Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Adana Porsiyon"
                      className="h-11"
                      data-testid="recipe-name-input"
                    />
                  </div>

                  {/* Recipe Type Selection */}
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                      Reçete Tipi
                    </Label>
                    <Select
                      value={formData.recipe_type}
                      onValueChange={(value) => setFormData({ ...formData, recipe_type: value, output_product_id: '' })}
                    >
                      <SelectTrigger className="h-11" data-testid="recipe-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cost_only">Sadece Maliyet Hesabı</SelectItem>
                        <SelectItem value="portion_transfer">Porsiyon Transfer (Hazırlık → Ocak)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {formData.recipe_type === 'portion_transfer' 
                        ? 'Hammaddeler Hazırlık stoğundan düşer, çıktı ürün Ocak stoğuna eklenir'
                        : 'Sadece maliyet hesabı yapılır, stok hareketi olmaz'}
                    </p>
                  </div>

                  {/* Output Product (for portion_transfer) */}
                  {formData.recipe_type === 'portion_transfer' && (
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                        Çıktı Ürün (Porsiyon) <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={formData.output_product_id}
                        onValueChange={(value) => setFormData({ ...formData, output_product_id: value })}
                      >
                        <SelectTrigger className="h-11" data-testid="output-product-select">
                          <SelectValue placeholder="Çıktı ürün seç" />
                        </SelectTrigger>
                        <SelectContent>
                          {outputProductOptions.map((product) => (
                            <SelectItem key={product.id} value={product.id}>
                              {product.name} ({product.unit})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Üretilen porsiyonlar bu ürünün Mağaza/Ocak stoğuna eklenir
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                        Kategori
                      </Label>
                      <div className="flex gap-2">
                        <Select
                          value={formData.category}
                          onValueChange={(value) => {
                            if (value === '__manage__') {
                              setCategoryManageDialogOpen(true);
                            } else {
                              setFormData({ ...formData, category: value });
                            }
                          }}
                        >
                          <SelectTrigger className="h-11 flex-1" data-testid="recipe-category-select">
                            <SelectValue placeholder="Kategori seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            {recipeCategories.map((cat) => (
                              <SelectItem key={cat.value} value={cat.value}>
                                {cat.label}
                              </SelectItem>
                            ))}
                            <div className="border-t border-border my-1" />
                            <SelectItem value="__manage__" className="text-muted-foreground">
                              <span className="flex items-center gap-2">
                                <Gear size={14} />
                                Kategorileri Yönet
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-11 w-11 shrink-0"
                          onClick={() => {
                            setNewCategoryName('');
                            setNewCategoryDescription('');
                            setEditingCategory(null);
                            setCategoryDialogOpen(true);
                          }}
                          title="Yeni Kategori Ekle"
                          data-testid="add-category-btn"
                        >
                          <FolderPlus size={18} />
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                        Satış Fiyatı (TL)
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.selling_price}
                        onChange={(e) => setFormData({ ...formData, selling_price: e.target.value })}
                        placeholder="0.00"
                        className="h-11"
                        data-testid="recipe-price-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                      {formData.recipe_type === 'portion_transfer' ? '1 Porsiyon İçin Gerekli Miktarlar' : 'Porsiyon Sayısı'}
                    </Label>
                    {formData.recipe_type === 'cost_only' ? (
                      <>
                        <Input
                          type="number"
                          min="1"
                          value={formData.portions_per_recipe}
                          onChange={(e) => setFormData({ ...formData, portions_per_recipe: e.target.value })}
                          placeholder="1"
                          className="h-11"
                          data-testid="recipe-portions-input"
                        />
                        <p className="text-xs text-muted-foreground">
                          Bu reçeteden kaç porsiyon çıkacağını belirtin
                        </p>
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <Factory size={14} className="inline mr-1" />
                        Aşağıda eklediğiniz miktarlar <strong>1 porsiyon</strong> içindir. Üretim sırasında girilen porsiyon sayısı ile çarpılır.
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                      Açıklama
                    </Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Reçete açıklaması..."
                      className="resize-none"
                      rows={2}
                      data-testid="recipe-description-input"
                    />
                  </div>

                  {/* Cost Summary */}
                  <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Toplam Maliyet:</span>
                      <span className="font-data font-semibold">{formatCurrency(totalCost)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Porsiyon Maliyeti:</span>
                      <span className="font-data font-semibold text-primary">{formatCurrency(costPerPortion)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Porsiyon Karı:</span>
                      <span className={`font-data font-semibold ${profit >= 0 ? 'text-secondary' : 'text-destructive'}`}>
                        {formatCurrency(profit)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Kar Marjı:</span>
                      <span className={`font-data font-semibold ${profitMargin >= 0 ? 'text-secondary' : 'text-destructive'}`}>
                        %{formatNumber(profitMargin, 1)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right Column - Ingredients */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                      {formData.recipe_type === 'portion_transfer' ? 'Hammadde Ekle (1 Porsiyon İçin)' : 'Malzeme / Alt Reçete Ekle'}
                    </Label>
                    <div className="flex gap-2">
                      <Popover open={ingredientSearchOpen} onOpenChange={setIngredientSearchOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={ingredientSearchOpen}
                            className="h-11 flex-1 justify-between font-normal"
                            data-testid="ingredient-select"
                          >
                            {ingredientForm.ingredient_type === 'recipe' && ingredientForm.recipe_id ? (
                              <span className="flex items-center gap-2">
                                <span>📋</span>
                                {recipes.find(r => r.id === ingredientForm.recipe_id)?.name || 'Reçete seçildi'}
                              </span>
                            ) : ingredientForm.product_id ? (
                              <span className="flex items-center gap-2">
                                <span>🥩</span>
                                {products.find(p => p.id === ingredientForm.product_id)?.name || 'Ürün seçildi'}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">Malzeme veya reçete ara...</span>
                            )}
                            <MagnifyingGlass className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-0" align="start">
                          <Command
                            filter={(value, search) => {
                              // Türkçe karakterleri normalize et
                              const normalize = (str) => str
                                .toLowerCase()
                                .replace(/ı/g, 'i')
                                .replace(/ğ/g, 'g')
                                .replace(/ü/g, 'u')
                                .replace(/ş/g, 's')
                                .replace(/ö/g, 'o')
                                .replace(/ç/g, 'c')
                                .replace(/İ/g, 'i');
                              
                              const normalizedValue = normalize(value);
                              const normalizedSearch = normalize(search);
                              
                              if (normalizedValue.includes(normalizedSearch)) return 1;
                              return 0;
                            }}
                          >
                            <CommandInput 
                              placeholder="Ürün veya reçete ara..." 
                              value={ingredientSearchQuery}
                              onValueChange={setIngredientSearchQuery}
                            />
                            <CommandList>
                              <CommandEmpty>Sonuç bulunamadı</CommandEmpty>
                              
                              {/* Ürünler Grubu */}
                              <CommandGroup heading="🥩 Ürünler">
                                {products
                                  .filter(p => formData.recipe_type !== 'portion_transfer' || p.id !== formData.output_product_id)
                                  .map((product) => (
                                    <CommandItem
                                      key={`product:${product.id}`}
                                      value={`${product.name} ürün`}
                                      onSelect={() => {
                                        setIngredientForm({
                                          ...ingredientForm,
                                          ingredient_type: 'product',
                                          product_id: product.id,
                                          recipe_id: '',
                                        });
                                        setIngredientSearchQuery('');
                                        setIngredientSearchOpen(false);
                                      }}
                                      className="cursor-pointer"
                                    >
                                      <div className="flex items-center justify-between w-full">
                                        <div className="flex items-center gap-2">
                                          <span>🥩</span>
                                          <span>{product.name}</span>
                                          <span className="text-xs text-muted-foreground">({product.unit})</span>
                                        </div>
                                        <span className="text-xs text-muted-foreground">
                                          Hzr: {formatNumber(product.prep_stock || 0)}
                                        </span>
                                      </div>
                                    </CommandItem>
                                  ))}
                              </CommandGroup>
                              
                              {/* Reçeteler Grubu - portion_transfer modunda gösterme */}
                              {formData.recipe_type !== 'portion_transfer' && recipes.length > 0 && (
                                <CommandGroup heading="📋 Reçeteler / Yarı Mamuller">
                                  {recipes
                                    .filter(r => !editingRecipe || r.id !== editingRecipe.id)
                                    .map((recipe) => (
                                      <CommandItem
                                        key={`recipe:${recipe.id}`}
                                        value={`${recipe.name} reçete`}
                                        onSelect={() => {
                                          setIngredientForm({
                                            ...ingredientForm,
                                            ingredient_type: 'recipe',
                                            recipe_id: recipe.id,
                                            product_id: '',
                                          });
                                          setIngredientSearchQuery('');
                                          setIngredientSearchOpen(false);
                                        }}
                                        className="cursor-pointer"
                                      >
                                        <div className="flex items-center justify-between w-full">
                                          <div className="flex items-center gap-2">
                                            <span>📋</span>
                                            <span>{recipe.name}</span>
                                          </div>
                                          <span className="text-xs text-muted-foreground">
                                            ₺{formatNumber(recipe.cost_per_portion || 0, 2)}/porsiyon
                                          </span>
                                        </div>
                                      </CommandItem>
                                    ))}
                                </CommandGroup>
                              )}
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <Input
                        type="number"
                        step="0.001"
                        value={ingredientForm.quantity}
                        onChange={(e) => setIngredientForm({ ...ingredientForm, quantity: e.target.value })}
                        placeholder={ingredientForm.ingredient_type === 'recipe' ? 'Porsiyon' : 'Miktar'}
                        className="h-11 w-24"
                        data-testid="ingredient-quantity-input"
                      />
                      <Button
                        type="button"
                        onClick={handleAddIngredient}
                        className="h-11"
                        data-testid="add-ingredient-button"
                      >
                        <Plus size={18} />
                      </Button>
                    </div>
                  </div>

                  <div className="border border-border rounded-lg overflow-hidden">
                    <Table className="data-table">
                      <TableHeader>
                        <TableRow className="border-b border-border bg-muted/30">
                          <TableHead className="py-2">Malzeme</TableHead>
                          <TableHead className="py-2 w-16">Tip</TableHead>
                          <TableHead className="text-right">Miktar</TableHead>
                          <TableHead className="text-right">Maliyet</TableHead>
                          <TableHead className="w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {formData.ingredients.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-8 text-muted-foreground text-sm">
                              Henüz malzeme eklenmedi
                            </TableCell>
                          </TableRow>
                        ) : (
                          formData.ingredients.map((ing, index) => {
                            const ingredientType = ing.ingredient_type || 'product';
                            const isRecipe = ingredientType === 'recipe';
                            
                            let name, unit, cost;
                            if (isRecipe) {
                              const subRecipe = recipes.find(r => r.id === ing.recipe_id);
                              name = subRecipe?.name || 'Bilinmeyen Reçete';
                              unit = 'porsiyon';
                              cost = subRecipe ? (subRecipe.cost_per_portion || 0) * ing.quantity : 0;
                            } else {
                              const product = products.find(p => p.id === ing.product_id);
                              name = product?.name || 'Bilinmeyen Ürün';
                              unit = product?.unit || '';
                              cost = product ? product.unit_price * ing.quantity : 0;
                            }
                            
                            const key = isRecipe ? `recipe-${ing.recipe_id}` : `product-${ing.product_id}`;
                            
                            return (
                              <TableRow key={key} className="border-b border-border/40">
                                <TableCell className="py-2 text-sm">
                                  {isRecipe ? '📋' : '🥩'} {name}
                                </TableCell>
                                <TableCell className="py-2 text-xs">
                                  <span className={`px-1.5 py-0.5 rounded ${isRecipe ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                                    {isRecipe ? 'Reçete' : 'Ürün'}
                                  </span>
                                </TableCell>
                                <TableCell className="text-right font-data text-sm">
                                  {formatNumber(ing.quantity, 3)} {unit}
                                </TableCell>
                                <TableCell className="text-right font-data text-sm">
                                  {formatCurrency(cost)}
                                </TableCell>
                                <TableCell>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleRemoveIngredient(ing)}
                                    className="h-8 w-8 text-destructive hover:text-destructive"
                                  >
                                    <X size={16} />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Ingredient Cost Pie Chart */}
                  {formData.ingredients.length > 0 && (
                    <div className="h-40">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={formData.ingredients.map((ing, index) => {
                              const ingredientType = ing.ingredient_type || 'product';
                              if (ingredientType === 'recipe') {
                                const subRecipe = recipes.find(r => r.id === ing.recipe_id);
                                return {
                                  name: subRecipe?.name || '',
                                  value: subRecipe ? (subRecipe.cost_per_portion || 0) * ing.quantity : 0,
                                };
                              } else {
                                const product = products.find(p => p.id === ing.product_id);
                                return {
                                  name: product?.name || '',
                                  value: product ? product.unit_price * ing.quantity : 0,
                                };
                              }
                            })}
                            cx="50%"
                            cy="50%"
                            innerRadius={30}
                            outerRadius={60}
                            dataKey="value"
                          >
                            {formData.ingredients.map((_, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value) => formatCurrency(value)}
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  İptal
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="recipe-submit">
                  {editingRecipe ? 'Güncelle' : 'Oluştur'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Production Dialog */}
        <Dialog open={productionDialogOpen} onOpenChange={setProductionDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Factory size={24} className="text-amber-600" />
                Porsiyon Üretimi
              </DialogTitle>
            </DialogHeader>
            
            {producingRecipe && (
              <div className="space-y-4">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="font-semibold">{producingRecipe.name}</p>
                  <p className="text-sm text-muted-foreground">
                    Çıktı: {producingRecipe.output_product_name}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Üretilecek Porsiyon Sayısı</Label>
                  <Input
                    type="number"
                    min="1"
                    value={productionQuantity}
                    onChange={(e) => setProductionQuantity(e.target.value)}
                    placeholder="20"
                    className="h-11"
                    data-testid="production-quantity-input"
                  />
                </div>

                {/* Requirements Table */}
                {productionQuantity && parseInt(productionQuantity) > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm">Gerekli Hammaddeler</Label>
                    <div className="border border-border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/30">
                            <TableHead className="py-2 text-xs">Hammadde</TableHead>
                            <TableHead className="text-right text-xs">Gerekli</TableHead>
                            <TableHead className="text-right text-xs">Hazırlık Stok</TableHead>
                            <TableHead className="text-center text-xs">Durum</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {productionRequirements.map((req) => (
                            <TableRow key={req.product_id} className="border-b border-border/40">
                              <TableCell className="py-2 text-sm">{req.product_name}</TableCell>
                              <TableCell className="text-right font-data text-sm">
                                {formatNumber(req.required, 3)} {req.unit}
                              </TableCell>
                              <TableCell className="text-right font-data text-sm">
                                {formatNumber(req.available, 3)} {req.unit}
                              </TableCell>
                              <TableCell className="text-center">
                                {req.sufficient ? (
                                  <CheckCircle size={20} className="text-green-600 inline" />
                                ) : (
                                  <Warning size={20} className="text-red-600 inline" />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    
                    {!canProduce && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                        <Warning size={16} className="inline mr-2" />
                        Yetersiz hazırlık stoğu! Üretim yapılamaz.
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Not (Opsiyonel)</Label>
                  <Textarea
                    value={productionNotes}
                    onChange={(e) => setProductionNotes(e.target.value)}
                    placeholder="Üretim notu..."
                    rows={2}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setProductionDialogOpen(false)}>
                İptal
              </Button>
              <Button
                onClick={handleProduction}
                disabled={!canProduce || productionLoading || !productionQuantity}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-production"
              >
                {productionLoading ? 'Üretiliyor...' : 'Üret'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* History Dialog */}
        <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
          <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <ClockCounterClockwise size={24} />
                Porsiyon Üretim Geçmişi
              </DialogTitle>
            </DialogHeader>
            
            <div className="border border-border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="py-3">Tarih</TableHead>
                    <TableHead>Reçete</TableHead>
                    <TableHead>Çıktı Ürün</TableHead>
                    <TableHead className="text-right">Porsiyon</TableHead>
                    <TableHead className="text-center">Durum</TableHead>
                    <TableHead className="text-right">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {portionTransfers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        Henüz üretim kaydı bulunmuyor
                      </TableCell>
                    </TableRow>
                  ) : (
                    portionTransfers.map((transfer) => (
                      <TableRow key={transfer.id} className="border-b border-border/40">
                        <TableCell className="py-3 text-sm">
                          {new Date(transfer.created_at).toLocaleString('tr-TR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </TableCell>
                        <TableCell className="font-medium">{transfer.recipe_name}</TableCell>
                        <TableCell className="text-muted-foreground">{transfer.output_product_name}</TableCell>
                        <TableCell className="text-right font-data">{transfer.portions_produced}</TableCell>
                        <TableCell className="text-center">
                          {transfer.status === 'completed' ? (
                            <span className="badge-pill bg-green-100 text-green-700 text-xs">Tamamlandı</span>
                          ) : (
                            <span className="badge-pill bg-red-100 text-red-700 text-xs">Geri Alındı</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {transfer.status === 'completed' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setRollingBackTransfer(transfer);
                                setRollbackReason('');
                                setRollbackDialogOpen(true);
                              }}
                              className="text-destructive hover:text-destructive"
                              data-testid={`rollback-${transfer.production_id}`}
                            >
                              <ArrowCounterClockwise size={16} className="mr-1" />
                              Geri Al
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </DialogContent>
        </Dialog>

        {/* Rollback Dialog */}
        <Dialog open={rollbackDialogOpen} onOpenChange={setRollbackDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-destructive flex items-center gap-2">
                <ArrowCounterClockwise size={24} />
                Üretimi Geri Al
              </DialogTitle>
            </DialogHeader>
            
            {rollingBackTransfer && (
              <div className="space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="font-semibold">{rollingBackTransfer.recipe_name}</p>
                  <p className="text-sm text-muted-foreground">
                    {rollingBackTransfer.portions_produced} porsiyon geri alınacak
                  </p>
                </div>
                
                <div className="text-sm text-muted-foreground">
                  <p>Bu işlem:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Çıktı ürün stoğundan {rollingBackTransfer.portions_produced} porsiyon düşer</li>
                    <li>Kullanılan hammaddeler hazırlık stoğuna geri eklenir</li>
                  </ul>
                </div>

                <div className="space-y-2">
                  <Label>Geri Alma Nedeni <span className="text-destructive">*</span></Label>
                  <Textarea
                    value={rollbackReason}
                    onChange={(e) => setRollbackReason(e.target.value)}
                    placeholder="Geri alma nedeninizi yazın..."
                    rows={2}
                    data-testid="rollback-reason-input"
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setRollbackDialogOpen(false)}>
                İptal
              </Button>
              <Button
                variant="destructive"
                onClick={handleRollback}
                disabled={!rollbackReason.trim()}
                data-testid="confirm-rollback"
              >
                Geri Al
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Reçeteyi Sil</DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              <strong>{deletingRecipe?.name}</strong> reçetesini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                İptal
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                data-testid="confirm-delete-recipe"
              >
                Sil
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* New Category Dialog */}
        <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingCategory ? 'Kategori Düzenle' : 'Yeni Kategori Oluştur'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="category-name" className="text-xs uppercase tracking-widest text-muted-foreground">
                  Kategori Adı <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="category-name"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Örn: Izgara Etler"
                  className="h-11"
                  data-testid="category-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category-desc" className="text-xs uppercase tracking-widest text-muted-foreground">
                  Açıklama (Opsiyonel)
                </Label>
                <Input
                  id="category-desc"
                  value={newCategoryDescription}
                  onChange={(e) => setNewCategoryDescription(e.target.value)}
                  placeholder="Kategori açıklaması..."
                  className="h-11"
                  data-testid="category-description-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setCategoryDialogOpen(false);
                  setEditingCategory(null);
                  setNewCategoryName('');
                  setNewCategoryDescription('');
                }}
              >
                İptal
              </Button>
              <Button
                onClick={editingCategory ? handleUpdateCategory : handleCreateCategory}
                disabled={!newCategoryName.trim() || categoryLoading}
                className="bg-primary hover:bg-primary/90"
                data-testid="save-category-btn"
              >
                {categoryLoading ? 'Kaydediliyor...' : (editingCategory ? 'Güncelle' : 'Oluştur')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Category Management Dialog */}
        <Dialog open={categoryManageDialogOpen} onOpenChange={(open) => {
          setCategoryManageDialogOpen(open);
          if (!open) setCategorySearchQuery('');
        }}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Gear size={24} />
                Reçete Kategorilerini Yönet
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Category Search */}
              <div className="relative">
                <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Kategori ara..."
                  className="pl-10 h-11"
                  value={categorySearchQuery}
                  onChange={(e) => setCategorySearchQuery(e.target.value)}
                  data-testid="category-search-input"
                />
              </div>
              
              {/* Category List */}
              <div className="border border-border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead className="py-3">Kategori Adı</TableHead>
                      <TableHead className="text-center">Reçete Sayısı</TableHead>
                      <TableHead className="text-center">Durum</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allCategories.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                          Henüz kategori bulunmuyor
                        </TableCell>
                      </TableRow>
                    ) : (
                      allCategories
                        .filter(cat => !categorySearchQuery || cat.label.toLowerCase().includes(categorySearchQuery.toLowerCase()))
                        .map((category) => (
                        <TableRow key={category.id || category.value} className="border-b border-border/40">
                          <TableCell className="py-3">
                            <div>
                              <span className="font-medium">{category.label}</span>
                              {category.description && (
                                <p className="text-xs text-muted-foreground mt-0.5">{category.description}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="badge-pill bg-muted text-muted-foreground text-xs">
                              {category.recipe_count || 0} reçete
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            {category.is_active !== false ? (
                              <span className="badge-pill bg-green-100 text-green-700 text-xs">Aktif</span>
                            ) : (
                              <span className="badge-pill bg-gray-100 text-gray-500 text-xs">Pasif</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => {
                                  setEditingCategory(category);
                                  setNewCategoryName(category.label);
                                  setNewCategoryDescription(category.description || '');
                                  setCategoryDialogOpen(true);
                                }}
                                title="Düzenle"
                                data-testid={`edit-category-${category.id}`}
                              >
                                <PencilSimple size={16} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleToggleCategoryActive(category)}
                                title={category.is_active !== false ? 'Pasife Al' : 'Aktifleştir'}
                                data-testid={`toggle-category-${category.id}`}
                              >
                                {category.is_active !== false ? (
                                  <X size={16} className="text-amber-600" />
                                ) : (
                                  <CheckCircle size={16} className="text-green-600" />
                                )}
                              </Button>
                              {(category.recipe_count === 0 || category.recipe_count === undefined) && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:text-destructive"
                                  onClick={() => handleDeleteCategory(category)}
                                  title="Sil"
                                  data-testid={`delete-category-${category.id}`}
                                >
                                  <Trash size={16} />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              
              {/* Add New Category Button */}
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setNewCategoryName('');
                  setNewCategoryDescription('');
                  setEditingCategory(null);
                  setCategoryDialogOpen(true);
                }}
                data-testid="add-category-from-manage"
              >
                <Plus size={18} className="mr-2" />
                Yeni Kategori Ekle
              </Button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCategoryManageDialogOpen(false)}>
                Kapat
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
